import { Outlet, useMatches } from '@tanstack/react-router';

import { AppHeader } from './app-header';
import { Breadcrumbs } from './breadcrumbs';

/**
 * Routes that take the whole window and supply their own chrome.
 *
 * Only the viewer, and it is the reason the exception exists: a prototype under
 * review has to be shown at the width it was designed for, which a max-width
 * column and a second header bar above it make impossible. Spelled as a route
 * id, exactly like `Breadcrumbs` does, rather than imported from the route
 * module — the route imports the root route, which renders this, and importing
 * back would close the cycle.
 */
const FULL_BLEED_ROUTE_IDS: ReadonlySet<string> = new Set([
  '/prototypes/$prototypeId',
]);

/**
 * The application shell: header, breadcrumb region, outlet.
 *
 * Rendered by the root route, so every page shares one column width and one set
 * of margins — except the viewer, which opts out entirely and owns the window.
 * The shell still owns the chrome; the viewer simply has its own.
 */
export const AppShell = () => {
  const matches = useMatches();
  const isFullBleed = matches.some((match) =>
    FULL_BLEED_ROUTE_IDS.has(match.routeId),
  );

  if (isFullBleed) {
    return (
      <div className="h-dvh overflow-hidden bg-viewer-canvas">
        <Outlet />
      </div>
    );
  }

  return (
    <div className="flex min-h-dvh flex-col bg-background">
      <AppHeader />

      <div className="mx-auto w-full max-w-7xl px-6 pt-6">
        <Breadcrumbs />
      </div>

      <main className="mx-auto w-full max-w-7xl flex-1 px-6 py-8">
        <Outlet />
      </main>
    </div>
  );
};
